export default class TemplateValidationService {
  constructor(TBService, EventService, $timeout) {
    'ngInject';
    this.$timeout = $timeout;
    this.tb = TBService;
    this.es = EventService;
    this.levels = {
      VALID: { key: '', value: 0, class: '', icon: '', warning: '' },
      INFO: { key: 'info', value: 1, class: 'info', icon: 'fa-info', warning: "The template's configuration can be improved!" },
      WARNING: { key: 'warning', value: 2, class: 'warning', icon: 'fa-exclamation-triangle', warning: "The template has some warnings!" },
      DANGER: { key: 'error', value: 3, class: 'danger', icon: 'fa-exclamation', warning: "The template has issues!" }
    };

    this._validationData = {};
    this._validatedConn = {};
    this._validationGroups = {
      TEMPLATE_SETTINGS: {
        title: "Template settings",
        validator: this.validateTemplateSettings,
        resolver: null,
        manualAction: 'Fix template settings',
        validConn: true
      },
      REQUIRED_FIELDS: {
        title: "Required fields",
        validator: this.validateRequiredFields,
        resolver: null,
        manualAction: 'Add the field'
      },
      TABLE_FIELDS: {
        title: "Table fields",
        validator: this.validateTableFields,
        resolver: {
          title: 'Remove field',
          action: (data) => {
            if (!confirm("Are you sure you want to remove the missing field from the form?")) return;
            this.$timeout(_ => this.es.dispatchEvent(this.es.actions.TOKEN_REMOVE, { token: { id: data.token.id, fieldSchema: { fieldType: data.token.fieldSchema?.fieldType} }} ));
          }
        }
      },
      FIELD_CHOICES: {
        title: "Field choices",
        validator: this.validateChoiceFields,
        resolver: {
          title: 'Remove choice',
          action: (data) => {
            if (!confirm("Are you sure you want to remove the choice from the form?")) return;
            this.$timeout(_ => this.es.dispatchEvent(this.es.actions.TOKEN_REMOVE, { token: { id: data.token.id, fieldSchema: { fieldType: data.token.fieldSchema?.fieldType} }} ));
          }
        }
      },
      SELECT_OPTIONS: {
        title: "Select options",
        validator: this.validateSelectOptions,
        resolver: {
          title: 'Fix options',
          action: (data) => {
            this.$timeout(_ => this.es.dispatchEvent(this.es.actions.CONFIG_TOKEN, { tokenId: data.token.id } ));
          }
        }
      },
      EMPTY_REFERENCES: {
        title: "Target tables",
        validator: this.validateEmptyReferences,
        resolver: {
          title: 'Remove table',
          action: (data) => {
            if (!confirm("Are you sure you want to remove the unused target table from the form?")) return;
            this.$timeout(_ => this.es.dispatchEvent(this.es.actions.REFERENCE_REMOVE_BEGIN, { reference: { id: data.reference.id, name: data.reference.name } }));
          }
        }
      },
      
    };
    this._validationGroupNames = Object.keys(this._validationGroups).reduce((acc, name) => { acc[name] = name; return acc; }, {});
  }

  get validationResults() {
    return this._validationData;
  }

  get validationGroups() {
    return this._validationGroupNames;
  }

  get validatedConn() {
    return this._validatedConn;
  }

  dispose() {
    this._validationData = {};
  }

  notifyUpdate() {
    this.es.dispatchEvent(this.es.actions.TEMPLATE_VALIDATED, this._validationData);
  }

  async validateGroup(groupName, data) {
    await this._validationGroups[groupName].validator(this, data);
    this.updateSummary();
    this.notifyUpdate();
  }

  // Template settings, only checking connection for now
  async validateTemplateSettings(service, data) {
    const validation = service._validationGroups.TEMPLATE_SETTINGS;
    let items = [];

    if (data.checkConn) {
      const res = await data.ss.validConnection(data.template);
      service._validatedConn = res.data;
      validation.validConn = service._validatedConn.isValid;
    }
    if (!validation.validConn) items.push({
      id: 0, 
      valid: false, 
      title: 'Invalid template connection!',
      data: { key: "INVALID_CONN", manualAction: "Update template connection" },
      level: service.levels.DANGER
    });
    
    // Signed templates configure upload/output fields per link (in the link wizard),
    // not on the template's references, so this template-level check does not apply.
    const isSignedTemplate = data.template.signTemplate === true;
    const uploadField = isSignedTemplate || !data.template.references.every(ref => !ref.uploadFID);
    if (!uploadField) items.push({
      id: 0, 
      valid: false, 
      title: 'No upload field was specified!',
      data: { key: "MISSING_UPLOAD_FIELD", manualAction: "Configure an upload field" }, 
      level: service.levels.WARNING
    });

    const valid = validation.validConn && uploadField;
    service._validationData.TEMPLATE_SETTINGS = { valid, title: validation.title, items, manualAction: validation.manualAction };
  }

  validateRequiredFields(service, data){
    const validation = service._validationGroups.REQUIRED_FIELDS;
    const res = service.tb.missingRequired;
    const items = Object.keys(res).reduce((acc, refId) => { 
      const ref = data.template.references.find(r => r.id == refId);
      res[refId].forEach((fId, index) => {
        const field = data.schemas[ref.tableId].fields.find(f => f.id == fId);
        acc.push({
          id: index, 
          valid: false, 
          title: `Missing required field '${field.label}' in '${ref.name}'`,
          data: { referenceId: refId, fieldId: fId },
          level: service.levels.DANGER
        });
      });
      return acc;
    }, []);
    const valid = !(items.length > 0);
    service._validationData.REQUIRED_FIELDS = { valid, title: validation.title, items, manualAction: validation.manualAction };
  }

  // TODO: validate orphans?
  // For now, getTemplateId returns an error when an orphan exists
  
  validateTableFields(service, data){
    const validation = service._validationGroups.TABLE_FIELDS;
    const items = data.template.tokens
      .filter(t => !t.fieldSchema)
      .map((t, index) => { 
        const tableName = data.template.references.find(r => r.id === t.referenceId)?.name || 'UNKOWN';
        return {
          id: index,
          valid: false,
          title: `Missing field id '${t.id}' in table '${tableName}'`,
          data: { token: t },
          level: service.levels.DANGER
        };
      });
    const valid = !(items.length > 0);
    service._validationData.TABLE_FIELDS = { valid, title: validation.title, items, resolver: validation.resolver };
  }

  validateChoiceFields(service, data){
    const validation = service._validationGroups.FIELD_CHOICES;
    const items = data.template.tokens
      .filter(t => t.control?.type === 'not-found' && t.options?.choice?.value)
      .map((t, index) => { 
        const tableName = data.template.references.find(r => r.id === t.referenceId)?.name || 'UNKOWN';
        return {
          id: index,
          valid: false,
          title: `Field missing choice [${t.options?.choice?.value}] in '${tableName}'`,
          data: { token: t },
          level: service.levels.DANGER
        };
      });
    const valid = !(items.length > 0);
    service._validationData.FIELD_CHOICES = { valid, title: validation.title, items, resolver: validation.resolver };
  }

  validateSelectOptions(service, data){
    const validation = service._validationGroups.SELECT_OPTIONS;
    const items = data.template.tokens
      .filter(t => t.options?.selectFieldIds?.length)
      .filter(t => t.options.selectFieldIds.some(fId => !t.control?.foreignKey?.defaultFields.some(f => f.id == fId)))
      .map((t, index) => { 
        return {
          id: index,
          valid: false,
          title: `Field is missing some select options`,
          data: { token: t },
          level: service.levels.DANGER
        };
      });
    const valid = !(items.length > 0);
    service._validationData.SELECT_OPTIONS = { valid, title: validation.title, items, resolver: validation.resolver };
  }

  validateEmptyReferences(service, data){
    const validation = service._validationGroups.EMPTY_REFERENCES;
    const items = data.template.references
      .filter(r => !data.template.tokens.some(t => t.referenceId === r.id))
      .map((r, index) => { 
        return {
          id: index,
          valid: false,
          title: `Reference '${r.name}' has no fields associated`,
          data: { reference: r },
          level: service.levels.WARNING
        };
      });
    const valid = !(items.length > 0);
    service._validationData.EMPTY_REFERENCES = { valid, title: validation.title, items, resolver: validation.resolver };
  }

  updateSummary() {
    const summary = Object.keys(this._validationData).filter(k => k != 'SUMMARY')
      .reduce((acc, key) => {
        const valData = this._validationData[key];
        const valid = acc.valid && valData.valid;
        const level = !valData.items.length ? acc.level :
          valData.items.reduce((lvl, i) =>  (i.level.value > lvl.value) ? { ...i.level }: lvl, acc.level);
        return { valid, level };
      }, { valid: true, level: this.levels.VALID });
    this._validationData["SUMMARY"] = summary;
  }
}